import {
  Tag,
  Address,
  CondominiumInformations,
  CondominiumDetails,
} from 'graphql/types'
import { minTwoDigits, toCurrency } from './number'
import { IconBuildingCircleMoneySign, IconArea, IconBed, IconCar } from 'icons'
import {
  getSubwayDistanceInfo,
  getElevatorsInfo,
  getListingBuildingInfos,
  TListingInfo,
} from './listing'
import { condominiumDataCardButtonEvent } from 'pages/regioes-atendidas/regiao/condominio/events'

export const getCondominiumInfos = (
  condominium: {
    tags: Array<Tag>
  } & CondominiumInformations,
) => {
  const {
    medianPropertyTax,
    minArea,
    maxArea,
    minRooms,
    maxRooms,
    medianDistanceToNearestSubwayInMeters,
    nearestSubwayStationsName,
    medianGarageSpots,
    medianFloorCount,
    medianUnitPerFloor,
    tags,
    medianElevators,
  } = condominium ?? {}

  if (!condominium) return []

  const subwayInfo = medianDistanceToNearestSubwayInMeters
    ? [
        getSubwayDistanceInfo(
          medianDistanceToNearestSubwayInMeters,
          nearestSubwayStationsName as string[],
        ),
      ]
    : []

  const elevatorsInfo = [getElevatorsInfo(medianElevators)]
  const buildingInfos = getListingBuildingInfos({
    floorCount: medianFloorCount,
    unitPerFloor: medianUnitPerFloor,
    tags,
  })

  return [
    {
      title: 'IPTU anual',
      text: 'IPTU do último ano',
      value: toCurrency(medianPropertyTax, { placeholder: '--' }),
      Icon: {
        Component: IconBuildingCircleMoneySign,
        title: 'Ícone: documento',
      },
    },
    {
      title: 'Menor planta',
      value: minArea ? `${minArea}m²` : '--',
      Icon: {
        Component: IconArea,
        title: 'Ícone: área',
      },
    },
    {
      title: 'Maior planta',
      value: maxArea ? `${maxArea}m²` : '--',
      Icon: {
        Component: IconArea,
        title: 'Ícone: área',
      },
    },
    {
      title: 'Mínimo de quartos',
      text: 'Incluí suíte',
      value: minTwoDigits(minRooms || 0),
      Icon: {
        Component: IconBed,
        title: 'Ícone: cama',
      },
    },
    {
      title: 'Máximo de quartos',
      text: 'Incluí suíte',
      value: minTwoDigits(maxRooms || 0),
      Icon: {
        Component: IconBed,
        title: 'Ícone: cama',
      },
    },
    ...subwayInfo,
    {
      title: 'Vagas de garagem',
      text: 'Vaga de condomínio',
      value: minTwoDigits(medianGarageSpots || 0),
      Icon: {
        Component: IconCar,
        title: 'Ícone: carro',
      },
    },
    ...buildingInfos,
    ...elevatorsInfo,
  ] as Array<TListingInfo>
}

export const getCondominiumDataCards = ({
  nameSlug,
  address,
  information,
}: CondominiumDetails) => {
  const { stateSlug, citySlug, neighborhoodSlug } = address || {}
  const {
    minMaintenanceFee,
    maxMaintenanceFee,
    medianMaintenanceFee,
    minPricePerArea,
    maxPricePerArea,
    medianPricePerArea,
    minPrice,
    maxPrice,
  } = information || {}

  const searchUrl =
    stateSlug && citySlug
      ? `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug!}/${nameSlug!}`
      : '/imoveis'

  const condominiumUrl = `${searchUrl}?condominioDe-${minMaintenanceFee!}-ate-${maxMaintenanceFee!}`
  const pricePerAreaUrl = `${searchUrl}?precoM2De-${minPricePerArea!}-ate-${maxPricePerArea!}`
  const priceUrl = `${searchUrl}?de-${minPrice!}-ate-${maxPrice!}`

  return [
    {
      title: 'Valor do condomínio',
      value: toCurrency(medianMaintenanceFee),
      button: {
        label: 'Buscar nesse valor',
        href: condominiumUrl,
        onClick: () =>
          condominiumDataCardButtonEvent(condominiumUrl, 'Buscar nesse valor'),
      },
    },
    {
      title: 'Média de valor do m²',
      value: toCurrency(medianPricePerArea),
      button: {
        label: 'Buscar nessa média',
        href: pricePerAreaUrl,
        onClick: () =>
          condominiumDataCardButtonEvent(pricePerAreaUrl, 'Buscar nessa média'),
      },
    },
    {
      title: 'Faixa de preço dos anúncios',
      value: `${toCurrency(minPrice)} a ${toCurrency(maxPrice)}`,
      button: {
        label: 'Buscar nessa faixa',
        href: priceUrl,
        onClick: () =>
          condominiumDataCardButtonEvent(priceUrl, 'Buscar nessa faixa'),
      },
    },
  ]
}

export const getMetaProperties = (
  condominium: CondominiumDetails,
  address: Address,
) => {
  const title = `${condominium.name!}, ${address.neighborhood!} - ${address.city!} | EmCasa`
  const description = `Conheça o ${condominium.name!}, ${address.neighborhood!} - ${address.city!}. Infraestrutura, imóveis à venda, como é morar no bairro e mais. EmCasa, todos os imóveis com os melhores corretores.`
  const canonical = `/regioes-atendidas/${address.stateSlug!}/${address.citySlug!}/${address.neighborhoodSlug!}/${condominium.nameSlug!}`

  return { title, description, url: canonical, canonical }
}
